import { useEffect, useMemo, useState } from 'react'
import { RotateCcw, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAllMaterials, useReorderList, useSaveMaterial } from '@/hooks/useInventoryMaster'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { reorderParamsFrom, toReorderNumber, type ReorderParams } from '@/lib/reorder'
import type { ApiMaterial } from '@/types/inventory'
import { ReorderCalcInputs, ReorderCalcResults, type CalcInputDef } from './ReorderCalcView'

type Key =
  | 'dailyConsumption'
  | 'orderLeadTimeDays'
  | 'releaseTimeDays'
  | 'safetyStockDays'
  | 'currentStock'
  | 'onOrder'
  | 'maxStock'
  | 'eoq'

const MAIN: { key: Key; label: string; unit: string }[] = [
  { key: 'dailyConsumption', label: 'Daily consumption', unit: 'units' },
  { key: 'orderLeadTimeDays', label: 'Order lead time', unit: 'days' },
  { key: 'releaseTimeDays', label: 'Release time', unit: 'days' },
  { key: 'safetyStockDays', label: 'Safety stock', unit: 'days' },
  { key: 'currentStock', label: 'Current stock', unit: 'units' },
  { key: 'onOrder', label: 'On order', unit: 'units' },
]

const QUANTITY_RULE: { key: Key; label: string; unit: string }[] = [
  { key: 'maxStock', label: 'Max stock (optional)', unit: 'units' },
  { key: 'eoq', label: 'EOQ (optional)', unit: 'units' },
]

/** The four settings "Save settings to material" writes back. */
const SAVED_KEYS = ['dailyConsumption', 'orderLeadTimeDays', 'releaseTimeDays', 'safetyStockDays'] as const

const EMPTY: Record<Key, string> = {
  dailyConsumption: '',
  orderLeadTimeDays: '',
  releaseTimeDays: '',
  safetyStockDays: '',
  currentStock: '',
  onOrder: '',
  maxStock: '',
  eoq: '',
}

const attr = (m: ApiMaterial, key: string): string => {
  const v = (m.attributes as Record<string, { value?: unknown }> | undefined)?.[key]?.value
  return v == null ? '' : String(v)
}

/**
 * Re-Order → Calculator (z8nrz7f7dz): the "Simplified Inventory Calculator".
 *
 * A pure what-if by default. Loading a material fills it from that
 * material's re-order settings and its current stock; with inventory.manage
 * the four settings can then be written back through the ordinary material
 * update (the same PATCH the inventory_unit editor saves with).
 */
export function ReorderCalculator() {
  const [values, setValues] = useState<Record<Key, string>>(EMPTY)
  const [materialId, setMaterialId] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [historyRate, setHistoryRate] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { can } = usePermission()
  const save = useSaveMaterial()

  const { data } = useAllMaterials({}, { enabled: pickerOpen || !!materialId })
  const materials = useMemo(() => data?.items ?? [], [data])
  const material = materials.find((m) => m.id === materialId) ?? null
  // The server's view of this material: its on-order quantity and, when no
  // rate is set, the rate it derives from 90 days of consumption.
  const reorder = useReorderList('all', undefined, material?.materialCode, { enabled: !!material })
  const row = reorder.data?.items.find((r) => r.id === materialId)

  // A material's own settings and stock, when one is loaded.
  const extras: Pick<ReorderParams, 'reorderPoint' | 'minStock'> = useMemo(
    () => ({
      reorderPoint: material ? toReorderNumber(attr(material, 'reorderPoint')) : null,
      minStock: material ? toReorderNumber(attr(material, 'minStock')) : null,
    }),
    [material],
  )

  function load(m: ApiMaterial) {
    setMaterialId(m.id)
    setHistoryRate(false)
    setSaved(false)
    setError(null)
    setValues({
      dailyConsumption: attr(m, 'dailyConsumption'),
      orderLeadTimeDays: attr(m, 'orderLeadTimeDays'),
      releaseTimeDays: attr(m, 'releaseTimeDays'),
      safetyStockDays: attr(m, 'safetyStockDays'),
      currentStock: String(m.availableQuantity ?? m.totalQuantity ?? 0),
      onOrder: String(m.reorder?.onOrder ?? 0),
      maxStock: attr(m, 'maxStock'),
      eoq: attr(m, 'eoq'),
    })
  }

  // The server's figures arrive a beat after the material: take its on-order,
  // and its history-based rate when the material sets none.
  // The hint is set only when the history rate is actually filled in, so a
  // refetch never labels a rate the planner typed as "averaged".
  useEffect(() => {
    if (!row) return
    const fill =
      values.dailyConsumption === '' && row.dailyConsumptionSource === 'history' && row.dailyConsumption != null
    setValues((v) => ({
      ...v,
      onOrder: String(row.onOrder),
      dailyConsumption: fill && v.dailyConsumption === '' ? String(row.dailyConsumption) : v.dailyConsumption,
    }))
    if (fill) setHistoryRate(true)
  }, [row])

  function reset() {
    setValues(EMPTY)
    setMaterialId('')
    setHistoryRate(false)
    setSaved(false)
    setError(null)
  }

  function saveToMaterial() {
    if (!material) return
    setError(null)
    const attributes = Object.fromEntries(
      SAVED_KEYS.map((k) => [k, { active: true, value: toReorderNumber(values[k]) }]),
    )
    save.mutate(
      { id: material.id, payload: { attributes } },
      {
        onSuccess: () => {
          setSaved(true)
          setHistoryRate(false)
          window.setTimeout(() => setSaved(false), 2500)
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'The settings could not be saved.'),
      },
    )
  }

  const params = reorderParamsFrom((k) => (k in values ? values[k as Key] : extras[k as keyof typeof extras]))
  // With a material loaded, stock is its live stock: shown, never typed over.
  const live = (key: Key) => !!material && (key === 'currentStock' || key === 'onOrder')
  const field = (f: { key: Key; label: string; unit: string }): CalcInputDef => ({
    ...f,
    label: live(f.key) ? `${f.label} (live)` : f.label,
    value: values[f.key],
    readOnly: live(f.key),
    hint:
      f.key === 'dailyConsumption' && historyRate
        ? 'Not set on the material: averaged from the last 90 days.'
        : f.key === 'currentStock' && material
          ? `Available now; ${material.totalQuantity ?? 0} on hand.`
          : undefined,
  })
  const set = (key: string, raw: string) => {
    if (live(key as Key)) return
    setValues((v) => ({ ...v, [key]: raw }))
    if (key === 'dailyConsumption') setHistoryRate(false)
  }

  return (
    <div className='grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]' data-testid='reorder-calculator-standalone'>
      <section className='space-y-5 rounded-xl border border-slate-200 bg-white p-5'>
        <div className='flex flex-wrap items-end gap-2'>
          <div className='min-w-[220px] flex-1 space-y-1' onFocusCapture={() => setPickerOpen(true)} onPointerDownCapture={() => setPickerOpen(true)}>
            <span className='text-xs font-medium text-slate-600'>Load from material (optional)</span>
            <SearchableSelect
              value={materialId}
              onChange={(id) => {
                const m = materials.find((x) => x.id === id)
                if (m) load(m)
              }}
              options={materials.map((m) => ({ value: m.id, label: `${m.materialId} · ${m.materialName}` }))}
              placeholder='Search materials…'
              emptyMessage={data ? 'No materials match.' : 'Loading materials…'}
            />
          </div>
          <Button variant='outline' size='sm' className='h-9 gap-1' onClick={reset}>
            <RotateCcw className='h-4 w-4' />
            Reset
          </Button>
        </div>

        <div className='space-y-2'>
          <h3 className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Inputs</h3>
          <ReorderCalcInputs fields={MAIN.map(field)} onChange={set} />
        </div>
        <div className='space-y-2'>
          <h3 className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Order-quantity rule</h3>
          <ReorderCalcInputs fields={QUANTITY_RULE.map(field)} onChange={set} />
          <p className='text-[11px] text-slate-500'>
            Orders up to max stock when set, otherwise the EOQ, otherwise back up to the reorder point.
          </p>
        </div>

        {material && can(PERMISSIONS.inventoryManage) && (
          <div className='flex flex-wrap items-center gap-2 border-t pt-4'>
            <Button size='sm' className='gap-1' onClick={saveToMaterial} disabled={save.isPending}>
              <Save className='h-4 w-4' />
              {save.isPending ? 'Saving…' : 'Save settings to material'}
            </Button>
            <span className='text-xs text-slate-500'>
              Daily consumption, lead, release and safety days for {material.materialId}.
            </span>
            {saved && <span className='text-xs font-medium text-green-600'>✓ Saved</span>}
          </div>
        )}
        {error && <p className='text-sm text-destructive'>{error}</p>}
      </section>

      <section className='space-y-3 rounded-xl border border-slate-200 bg-slate-50/50 p-5'>
        <h3 className='text-sm font-semibold text-slate-900'>
          Results{material && <span className='font-normal text-slate-500'> · {material.materialId}</span>}
        </h3>
        <ReorderCalcResults
          params={params}
          currentStock={toReorderNumber(values.currentStock) ?? 0}
          onHand={material ? (material.totalQuantity ?? 0) : undefined}
          onOrder={toReorderNumber(values.onOrder) ?? 0}
        />
      </section>
    </div>
  )
}
